import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { updateOrganizationTrial, StartTrialPayload } from '../../Services/BorgAPIClient';

const DURATION_MONTHS = 'durationMonths';
const TRIAL_END_DATE = 'trialEndDate';
const DATE_TIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

interface StartTrialModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  organizationId: string;
}

const StartTrialModal: React.FC<StartTrialModalProps> = ({ open, onClose, onConfirm, organizationId }) => {
  const [mode, setMode] = useState(DURATION_MONTHS);
  const [months, setMonths] = useState(3);
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode(DURATION_MONTHS);
      setMonths(3);
      const d = new Date();
      d.setMonth(d.getMonth() + 3);
      setEndDate(d);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setError(null);
    let payload: StartTrialPayload;
    if (mode === DURATION_MONTHS) {
      if (months < 1) {
        setError('Months must be at least 1.');
        return;
      }
      payload = { startTrial: true, durationMonths: months };
    } else {
      const dateToUse = endDate ?? new Date();
      if (dateToUse <= new Date()) {
        setError('Trial end date must be in the future.');
        return;
      }
      payload = { startTrial: true, trialEndDate: dateToUse.toISOString() };
    }
    try {
      await updateOrganizationTrial(organizationId, payload);
      onConfirm();
    } catch (err) {
      setError('Failed to start trial.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Start Trial</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0, width: '340px' }}>
        <RadioGroup value={mode} onChange={(e) => { setMode(e.target.value as string); setError(null); }}>
          <FormControlLabel value={DURATION_MONTHS} control={<Radio />} label="Trial Duration - Months" />
          <FormControlLabel value={TRIAL_END_DATE} control={<Radio />} label="Trial End - Date" />
        </RadioGroup>
        {mode === DURATION_MONTHS && (
          <TextField
            label="Months"
            type="number"
            variant="standard"
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 1 }}
            fullWidth
            margin="dense"
          />
        )}
        {mode === TRIAL_END_DATE && (
          <TextField
            label="Schedule Trial End"
            type="datetime-local"
            variant="standard"
            value={endDate ? moment(endDate).format(DATE_TIME_LOCAL_FORMAT) : ''}
            onChange={(e) => setEndDate(moment(e.target.value).toDate())}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
          />
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartTrialModal;
